import { Action, Badge, Modal, SimpleSelect } from 'corso-admin-ui';
import { CrewMerchantUi, isTruthy } from 'corso-types';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import DescriptionList from '~/components/DescriptionList';
import { customFieldValueTypeFormatter } from '~/components/FormatCustomField';
import useClaimLineItemUpdate, {
  ClaimLineItemUpdateReason,
} from '~/features/claimReview/hooks/useClaimLineItemUpdate';
import { useClaimReasons } from '~/hooks/useClaimReasons';

function ClaimLineItemEditReason({
  claimLineItem,
  show,
  onClose,
}: {
  claimLineItem: CrewMerchantUi.ClaimLineItem;
  show: boolean;
  onClose: () => void;
}) {
  const { data: storeClaimReason } = useClaimReasons();
  const { mutateAsync: onMutate, isPending } = useClaimLineItemUpdate(
    claimLineItem.id,
  );

  const { handleSubmit, control, watch, setValue } =
    useForm<ClaimLineItemUpdateReason>({
      defaultValues: {
        id: claimLineItem.reason.id,
        detailId: claimLineItem.reason.detail?.id ?? null,
      },
    });

  if (!storeClaimReason) return null;

  const reasonOptions = storeClaimReason.map((reason) => ({
    label: reason.name,
    value: `${reason.id}`,
  }));

  const selectedReasonId = watch('id');

  const reasonDetailOptions =
    storeClaimReason
      .find((reason) => reason.id === selectedReasonId)
      ?.claimReasonDetails.map((detail) => ({
        label: detail.name,
        value: `${detail.id}`,
      })) ?? [];

  const saveChanges = () => {
    handleSubmit((reason) => {
      onMutate({
        kind: 'reason',
        reason,
      })
        .then(() => onClose())
        .catch((error) => console.error(error));
    })().catch(console.error);
  };

  return (
    <Modal show={show} onClose={onClose} title="Edit Reason">
      <Controller
        name="id"
        control={control}
        render={({ field }) => (
          <SimpleSelect
            options={reasonOptions}
            label="Reason"
            onChange={(value) => {
              field.onChange(parseInt(value, 10));
              setValue('detailId', null); // Reset detail when reason changes
            }}
            value={`${field.value}`}
          />
        )}
      />
      {reasonDetailOptions.length > 0 && (
        <Controller
          name="detailId"
          control={control}
          render={({ field }) => (
            <SimpleSelect
              placeholder="Select reason detail (optional)"
              options={reasonDetailOptions}
              label="Reason Detail"
              onChange={(value) => {
                field.onChange(parseInt(value, 10) || null);
              }}
              value={`${field.value}`}
            />
          )}
        />
      )}
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
        <Action variant="ghost" onClick={onClose}>
          Cancel
        </Action>
        <Action
          variant="primary"
          loading={isPending}
          disabled={isPending}
          onClick={saveChanges}
        >
          Save
        </Action>
      </div>
    </Modal>
  );
}

export default function ClaimLineItemDetails({
  claimLineItem,
}: {
  claimLineItem: CrewMerchantUi.ClaimLineItem;
}) {
  const [showEditReason, setShowEditReason] = useState(false);
  const descriptions = [
    {
      term: 'Status',
      details: (
        <Badge>
          {claimLineItem.status.name}
          {claimLineItem.status.detail.name &&
            ` - ${claimLineItem.status.detail.name}`}
        </Badge>
      ),
    },
    {
      term: 'Reason',
      details: (
        <div className="flex items-center gap-2">
          <Badge>
            {claimLineItem.reason.name}
            {claimLineItem.reason.detail?.name &&
              ` - ${claimLineItem.reason.detail.name}`}
          </Badge>
          <Action
            accessibilityLabel="Edit reason"
            icon="edit"
            variant="ghost"
            onClick={() => setShowEditReason(true)}
          />
        </div>
      ),
    },
    !!claimLineItem.comments?.length && {
      term: 'Comment',
      details: claimLineItem.comments,
    },
    !!claimLineItem.noteToCustomer?.length && {
      term: 'Note to Customer',
      details: claimLineItem.noteToCustomer,
    },
  ].filter(isTruthy);

  return (
    <div className="space-y-6">
      <ClaimLineItemEditReason
        claimLineItem={claimLineItem}
        show={showEditReason}
        onClose={() => setShowEditReason(false)}
      />
      <DescriptionList descriptions={descriptions} layoutType="list" />
      {!!claimLineItem.customFields.length && (
        <div className="space-y-2">
          <p className="text-xs font-semibold">Custom Fields</p>
          <DescriptionList
            descriptions={claimLineItem.customFields.map(
              ({ displayName, valueType, value }) => ({
                term: displayName,
                details: customFieldValueTypeFormatter[valueType](value),
              }),
            )}
            layoutType="list"
          />
        </div>
      )}
    </div>
  );
}
